import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class DocumentUtilsService {

  constructor() { }

  /**
   * Valida CPF usando o algoritmo dos dígitos verificadores
   */
  private isValidCpf(cpf: string): boolean {
    const cleaned = cpf.replace(/\D/g, '');

    if (cleaned.length !== 11) {
      return false;
    }

    // Verifica se todos os dígitos são iguais (ex: 111.111.111-11)
    if (/^(\d)\1{10}$/.test(cleaned)) {
      return false;
    }

    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleaned.charAt(i), 10) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) {
      remainder = 0;
    }
    if (remainder !== parseInt(cleaned.charAt(9), 10)) {
      return false;
    }

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleaned.charAt(i), 10) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) {
      remainder = 0;
    }
    if (remainder !== parseInt(cleaned.charAt(10), 10)) {
      return false;
    }

    return true;
  }

  /**
   * Valida CNPJ usando o algoritmo dos dígitos verificadores
   */
  private isValidCnpj(cnpj: string): boolean {
    const cleaned = cnpj.replace(/\D/g, '');

    if (cleaned.length !== 14) {
      return false;
    }

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleaned)) {
      return false;
    }

    // Validação do primeiro dígito verificador
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleaned.charAt(i), 10) * weights1[i];
    }
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    if (digit1 !== parseInt(cleaned.charAt(12), 10)) {
      return false;
    }

    // Validação do segundo dígito verificador
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    sum = 0;
    for (let i = 0; i < 13; i++) {
      sum += parseInt(cleaned.charAt(i), 10) * weights2[i];
    }
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    if (digit2 !== parseInt(cleaned.charAt(13), 10)) {
      return false;
    }

    return true;
  }

  /**
   * Validador personalizado para CPF com verificação completa dos dígitos verificadores
   * @returns ValidatorFn para validar CPF (11 dígitos) ou CNPJ (14 dígitos)
   */
  cpfCnpjValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null; // CPF não é obrigatório se o valor for vazio
      }
      
      // Remove todos os caracteres não numéricos
      const digits = value.toString().replace(/\D/g, '');
      
      if (digits.length === 0) {
        return null; // Campo vazio
      }

      // Verifica se é um CPF (11 dígitos) ou CNPJ (14 dígitos) e valida
      if (digits.length === 11) {
        return this.isValidCpf(digits) ? null : { invalidCpfCnpj: true };
      } else if (digits.length === 14) {
        return this.isValidCnpj(digits) ? null : { invalidCpfCnpj: true };
      }
      
      // Quantidade de dígitos inválida
      return { invalidCpfCnpj: true };
    };
  }

  /**
   * Validador apenas para CPF com verificação completa
   */
  cpfValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      const digits = value.toString().replace(/\D/g, '');
      if (digits.length === 0) {
        return null;
      }
      return this.isValidCpf(digits) ? null : { invalidCpf: true };
    };
  }

  /**
   * Validador apenas para CNPJ com verificação completa
   */
  cnpjValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      const digits = value.toString().replace(/\D/g, '');
      if (digits.length === 0) {
        return null;
      }
      return this.isValidCnpj(digits) ? null : { invalidCnpj: true };
    };
  }

  /**
   * Aplica máscara de CPF ou CNPJ dependendo do comprimento
   * @param value Valor a ser formatado
   * @returns Valor formatado com a máscara apropriada
   */
  setCpfCnpjMask(value: string): string {
    // Remover caracteres não numéricos
    const digits = value.toString().replace(/\D/g, '');
    
    // Aplicar máscara de CPF (11 dígitos)
    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    }
    // Aplicar máscara de CNPJ (14 dígitos)
    else if (digits.length === 14) {
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    }
    
    // Se não for CPF nem CNPJ, retorna o valor original
    return value;
  }

  /**
   * Método para processar entrada de CPF em tempo real
   * @param event Evento de input
   * @param formControl Controle do formulário para atualizar
   */
  onCpfCnpjInput(event: Event, formControl: AbstractControl): void {
    const inputEvent = event as InputEvent;
    const target = inputEvent.target as HTMLInputElement;
    
    // Remove caracteres não numéricos para processar
    const digits = target.value.replace(/\D/g, '');
    
    // Se tiver 11 dígitos (CPF) ou 14 dígitos (CNPJ), aplica a máscara correspondente
    if (digits.length === 11 || digits.length === 14) {
      // Aplicar a máscara adequada
      const maskedValue = this.setCpfCnpjMask(digits);
      
      // Atualiza o valor do campo com a máscara aplicada
      formControl.setValue(maskedValue, { emitEvent: false });
      
      // Atualizar diretamente o DOM para garantir consistência
      target.value = maskedValue;
      
      // Validar o campo após a aplicação da máscara
      formControl.updateValueAndValidity();
    }
  }

  /**
   * Formata apenas CPF (usado para compatibilidade com código existente)
   * @param value Valor a ser formatado
   * @returns CPF formatado
   */
  setCpfMask(value: string): string {
    const digits = value.toString().replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    }
    return value;
  }

  /**
   * Formata apenas CNPJ (usado para compatibilidade com código existente)
   * @param value Valor a ser formatado
   * @returns CNPJ formatado
   */
  setCnpjMask(value: string): string {
    const digits = value.toString().replace(/\D/g, '');
    if (digits.length === 14) {
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    }
    return value;
  }
} 